import { Button, Card, CardContent, Grid, GridColumn, Heading, Icon, Notice, Separator, Text } from 'telia-front-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import DealerMandateListItems from '../dealer/DealerMandateListItems';
import MandateSearch from '../mandate/MandateSearch';
import useStores from '../stores/UseStores';
import PaddedSpinner from '../util/PaddedSpinner';
import WrapWithContainerInFullPage from '../wrapper/WrapWithContainerInFullPage';

const DealerMandatesPage = observer((): React.JSX.Element => {
  const {
    dealerRepresentationStore,
    translateStore: { translate },
  } = useStores();
  const { isLoading, isRepresentativeSelected, isRepresentativeChanging, userMandatesNotIncluded, representation, b2bCustomers, b2cCustomers } =
    dealerRepresentationStore;
  const [searchFilter, setSearchFilter] = React.useState<string>('');

  React.useEffect(() => {
    dealerRepresentationStore.init();
  }, []);

  const onSearchChange = React.useCallback((value: string) => {
    setSearchFilter(value.toLowerCase());
  }, []);

  const applySearchFilter = (name: string) => {
    return name.toLowerCase().indexOf(searchFilter) > -1;
  };

  const getB2cCustomers = () => {
    if (!searchFilter) {
      return b2cCustomers;
    }
    return b2cCustomers.filter((c) => applySearchFilter(c.displayName));
  };

  const getB2bCustomers = () => {
    if (!searchFilter) {
      return b2bCustomers;
    }
    return b2bCustomers.filter((c) => applySearchFilter(c.displayName));
  };

  if (isLoading) {
    return <PaddedSpinner />;
  }

  if (!isRepresentativeSelected) {
    return <Notice children={translate('dealer.mandates.notSelected')} />;
  }

  const personName = `${representation!.person!.firstName} ${representation!.person!.lastName}`;
  const personalCodeText = `${representation!.person!.personalCode} - ${representation!.person!.personalCodeCountryCode}`;

  return (
    <WrapWithContainerInFullPage>
      <Text>
        <Heading id="mandates-title" className="h4">
          {translate('mandates.title')}
        </Heading>
        <Card padding="small">
          <CardContent>
            <Grid noWrap={true} gutter="small" align={['middle-xs']}>
              <GridColumn width={['min']}>
                <Icon modifiers="#contact" size="xs" />
              </GridColumn>
              <GridColumn width={['max']}>
                <p className="text-bold">{personName}</p>
                <p className="text-tiny">{`${translate('dealer.mandates.representer')}, ${personalCodeText}`}</p>
              </GridColumn>
            </Grid>
          </CardContent>
        </Card>
        <Button
          disabled={isRepresentativeChanging}
          text={translate('dealer.mandates.clearRepresentation')}
          onClick={() => dealerRepresentationStore.clearRepresentation()}
          type="employee"
          block={true}
        />
        <Separator color="invisible" spacing="xsmall" />
        {userMandatesNotIncluded && <Notice children={translate('dealer.mandates.userMandatesNotIncluded')} />}
        {b2bCustomers.length >= 10 && <MandateSearch onSearchChange={onSearchChange} />}
        <DealerMandateListItems b2cMandates={getB2cCustomers()} b2bMandates={getB2bCustomers()} />
      </Text>
    </WrapWithContainerInFullPage>
  );
});

export default DealerMandatesPage;
